import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { UserRewindScreenFragmentFragment } from 'types/graphql-api.generated';

import RewindModalComponent from 'common/components/RewindModal/Component';
import {
  REWIND_LOGO_HEADER_HEIGHT,
  REWIND_LOGO_HEADER_WIDTH
} from 'common/components/RewindModal/constants';
import { computeImgSize } from 'common/components/RewindModal/helper';
import {
  useNavigation,
  useOpeCustomization,
  useUserData
} from 'common/components/RewindModal/hooks';
import { IMG_REWIND_2025 } from 'common/configuration/constants';
import 'common/constants/LogoMain';
import { ModalNames } from 'common/module/ui/lazy-modal/constant-types';
import buildReactDomModalModule from 'common/module/ui/lazy-modal/react-dom-modal-module';
import ReactModal, {
  ReactModalProps
} from 'common/module/ui/lazy-modal/react-modal';
import { CloseModal, OnCloseParams } from 'common/module/ui/lazy-modal/types';
import { setBgImageStyle } from 'common/tools/react/background';

import styles from './styles.module.scss';

export type RewindModalParams = {
  screenName: ScreenNames;
  flashbackType?: number;
  trackingScreenName?: string;
};
export type LogoProps = Pick<
  ReactModalProps,
  'image' | 'imageWidth' | 'imageHeight'
>;
export type DataLoadingStatus = 'loading' | 'loaded' | 'error';
type RewindModalProps = {
  closeModal: CloseModal;
  onCloseParams: OnCloseParams;
};

const RewindModal = ({ closeModal, onCloseParams }: RewindModalProps) => {
  const { loaded, ope, images } = useOpeCustomization();
  const { dataLoadingStatus, getData, setDataLoadingStatus, userData } =
    useUserData(ope?.suggested_entities_list);

  const apiScreens = useMemo((): UserRewindScreenFragmentFragment[] => {
    const screens = userData?.me?.user?.social?.rewind?.screens;
    return (
      (screens?.filter(Boolean) as UserRewindScreenFragmentFragment[]) ?? []
    );
  }, [userData]);

  const {
    handleNext: baseHandleNext,
    handleStep,
    screenName,
    setScreens,
    step,
    steps
  } = useNavigation([]);

  const [backgroundImage, setBackgroundImage] = useState<
    CSSProperties | undefined
  >({});
  const [headerLogo, setHeaderLogo] = useState<LogoProps>({
    image: '',
    imageWidth: 0,
    imageHeight: 0
  });
  const [screenList, setScreenList] = useState<ScreenNames[]>([]);
  const [flashbackType, setFlashbackType] = useState<number | undefined>();

  const handleNext = useCallback(() => {
    if (screenName === 'screen_bilan_user') {
      const rewindCaptifIndex = screenList.indexOf('rewind_captif');
      if (rewindCaptifIndex !== -1) {
        handleStep(rewindCaptifIndex);
        return;
      }
    }
    if (flashbackType === 6 && screenName === 'screen_series_top3') {
      const rewindCaptifIndex = screenList.indexOf('rewind_captif');
      if (rewindCaptifIndex !== -1) {
        handleStep(rewindCaptifIndex);
        return;
      }
    }
    baseHandleNext();
  }, [screenName, screenList, baseHandleNext, handleStep, flashbackType]);

  const rewindLogo = useMemo(
    () => (ope?.main?.logo?.url?.length ? ope.main.logo.url : IMG_REWIND_2025),
    [ope]
  );
  const rewindLogoWidth = useMemo(
    () => ope?.main?.logo?.width ?? REWIND_LOGO_HEADER_WIDTH,
    [ope]
  );
  const rewindLogoHeight = useMemo(
    () => ope?.main?.logo?.height ?? REWIND_LOGO_HEADER_HEIGHT,
    [ope]
  );

  useEffect(() => {
    if (loaded && !dataLoadingStatus && !userData) {
      getData();
    } else if (userData && dataLoadingStatus === 'loading') {
      setDataLoadingStatus('loaded');
    }
  }, [loaded, dataLoadingStatus, userData, getData, setDataLoadingStatus]);

  useEffect(() => {
    if (dataLoadingStatus === 'loaded') {
      const finalScreenList: ScreenNames[] = ['screen_upload_data'];

      const flashbackScreen = apiScreens.find(s => s.name === 'flashback_type');
      const flashbackTypeValue = flashbackScreen?.data?.flashback_type;
      setFlashbackType(flashbackTypeValue);

      const hasActorScreen =
        flashbackTypeValue === 6
          ? apiScreens.some(
              s =>
                s?.name?.includes('_actor') ||
                s?.name === 'screen_top_actor_2025'
            )
          : apiScreens.some(s => s?.name?.includes('_actor'));
      const hasDirectorScreen =
        flashbackTypeValue === 6
          ? apiScreens.some(
              s =>
                s?.name?.includes('_director') ||
                s?.name === 'screen_top_director_2025'
            )
          : apiScreens.some(s => s?.name?.includes('_director'));

      let hasAddedTopContent = false;
      let hasAddedCelebrities = false;

      apiScreens.forEach(screen => {
        if (!screen || !screen.name) return;

        if (screen.name.startsWith('screen_top10_')) {
          const baseName = screen.name.replace('screen_', '');
          finalScreenList.push(
            `${baseName}_intro` as ScreenNames,
            `${baseName}_reveal` as ScreenNames
          );
        } else if (
          screen.name.startsWith('screen_top_theater_') ||
          screen.name.startsWith('screen_top_series_online')
        ) {
          finalScreenList.push(screen.name as ScreenNames);
        } else if (flashbackTypeValue === 6) {
          if (screen.name === 'screen_top_global_movies_2025') {
            finalScreenList.push(
              'screen_top_global_movies_2025_intro' as ScreenNames
            );
            finalScreenList.push(
              'screen_top_global_movies_2025_reveal_1' as ScreenNames,
              'screen_top_global_movies_2025_reveal_2' as ScreenNames,
              'screen_top_global_movies_2025_reveal_3' as ScreenNames
            );
          } else if (screen.name === 'screen_top_global_series_2025') {
            finalScreenList.push(
              'screen_top_global_series_2025_intro' as ScreenNames
            );
            finalScreenList.push(
              'screen_top_global_series_2025_reveal_1' as ScreenNames,
              'screen_top_global_series_2025_reveal_2' as ScreenNames,
              'screen_top_global_series_2025_reveal_3' as ScreenNames
            );
          } else if (
            screen.name === 'screen_top_actor_2025' ||
            screen.name === 'screen_top_director_2025'
          ) {
            if (!hasAddedCelebrities) {
              const celebritySlidesToAdd: ScreenNames[] = [];
              if (hasActorScreen || hasDirectorScreen)
                celebritySlidesToAdd.push('favorite_celebrity_intro');
              if (hasActorScreen)
                celebritySlidesToAdd.push('favorite_celebrity_actor');
              if (hasDirectorScreen)
                celebritySlidesToAdd.push('favorite_celebrity_director');
              finalScreenList.push(...celebritySlidesToAdd);
              hasAddedCelebrities = true;
            }
          } else if (
            screen.name === 'screen_movie_top3' ||
            screen.name === 'screen_series_top3'
          ) {
            if (screen.name === 'screen_movie_top3') {
              finalScreenList.push('screen_movie_top3_intro' as ScreenNames);
            } else if (screen.name === 'screen_series_top3') {
              finalScreenList.push('screen_series_top3_intro' as ScreenNames);
            }
            finalScreenList.push(screen.name as ScreenNames);
          } else if (
            !screen.name.startsWith('screen_top_') &&
            !screen.name.startsWith('screen_favorite_') &&
            !screen.name.startsWith('screen_top10_') &&
            screen.name !== 'screen_movie_top3' &&
            screen.name !== 'screen_series_top3'
          ) {
            finalScreenList.push(screen.name as ScreenNames);
          }
        } else {
          if (
            screen.name.startsWith('screen_top_') &&
            !screen.name.includes('_theater_') &&
            !screen.name.includes('_global_') &&
            !hasAddedTopContent
          ) {
            finalScreenList.push(
              'top_content_intro',
              'top_content_reveal_1',
              'top_content_reveal_2',
              'top_content_reveal_3'
            );
            hasAddedTopContent = true;
          } else if (
            screen.name.startsWith('screen_favorite_') &&
            !hasAddedCelebrities
          ) {
            const celebritySlidesToAdd: ScreenNames[] = [];
            if (hasActorScreen || hasDirectorScreen)
              celebritySlidesToAdd.push('favorite_celebrity_intro');
            if (hasActorScreen)
              celebritySlidesToAdd.push('favorite_celebrity_actor');
            if (hasDirectorScreen)
              celebritySlidesToAdd.push('favorite_celebrity_director');
            finalScreenList.push(...celebritySlidesToAdd);
            hasAddedCelebrities = true;
          } else if (
            !screen.name.startsWith('screen_top_') &&
            !screen.name.startsWith('screen_favorite_') &&
            !screen.name.startsWith('screen_top10_')
          ) {
            finalScreenList.push(screen.name as ScreenNames);
          }
        }
      });

      if (flashbackTypeValue === 6) {
        const top3MoviesIntroIndex = finalScreenList.indexOf(
          'screen_movie_top3_intro'
        );
        const top3SeriesIntroIndex = finalScreenList.indexOf(
          'screen_series_top3_intro'
        );
        const top3MoviesIndex = finalScreenList.indexOf('screen_movie_top3');
        const top3SeriesIndex = finalScreenList.indexOf('screen_series_top3');

        let insertIndex = -1;
        if (top3MoviesIntroIndex !== -1) {
          insertIndex = top3MoviesIntroIndex;
        } else if (top3SeriesIntroIndex !== -1) {
          insertIndex = top3SeriesIntroIndex;
        } else if (top3MoviesIndex !== -1) {
          insertIndex = top3MoviesIndex;
        } else if (top3SeriesIndex !== -1) {
          insertIndex = top3SeriesIndex;
        }

        if (insertIndex !== -1) {
          finalScreenList.splice(insertIndex, 0, 'screen_conclusion_global');
        } else {
          finalScreenList.push('screen_conclusion_global');
        }
      } else if (flashbackTypeValue >= 1 && flashbackTypeValue <= 5) {
        const top3Screens: ScreenNames[] = [
          'screen_movie_top3_intro',
          'screen_series_top3_intro',
          'screen_movie_top3',
          'screen_series_top3'
        ];

        let insertIndex = -1;
        for (let i = 0; i < finalScreenList.length; i++) {
          if (top3Screens.includes(finalScreenList[i])) {
            insertIndex = i;
            break;
          }
        }

        if (insertIndex !== -1) {
          finalScreenList.splice(insertIndex, 0, 'screen_conclusion');
        } else {
          finalScreenList.push('screen_conclusion');
        }
      }

      if (flashbackTypeValue !== 6) {
        finalScreenList.push('screen_bilan_user');
      }
      finalScreenList.push('rewind_captif');

      setScreens(finalScreenList);
      setScreenList(finalScreenList);
    }
  }, [apiScreens, dataLoadingStatus, setScreens, setScreenList]);

  useEffect(() => {
    if (loaded && ope) {
      setBackgroundImage(setBgImageStyle(ope.main?.background));
    }
  }, [loaded, ope]);

  useEffect(() => {
    if (loaded) {
      const [imageWidth, imageHeight] = computeImgSize(
        REWIND_LOGO_HEADER_WIDTH,
        REWIND_LOGO_HEADER_HEIGHT,
        rewindLogoWidth,
        rewindLogoHeight
      );
      const logo = { image: rewindLogo, imageWidth, imageHeight };
      setHeaderLogo(logo);
    }
  }, [loaded, rewindLogo, rewindLogoWidth, rewindLogoHeight]);

  const isNavigationVisible = useMemo(
    () =>
      !['screen_upload_data', 'rewind_captif', 'flashback_type'].includes(
        screenName
      ),
    [screenName]
  );

  const hiddenScreens: ScreenNames[] = useMemo(
    () => ['screen_upload_data', 'rewind_captif', 'flashback_type'],
    []
  );

  const paginatedScreens = useMemo(() => {
    const filtered = screenList.filter(s => !hiddenScreens.includes(s));

    // For type 6, exclude top3 intro screens as they are skipped
    const filteredForPagination =
      flashbackType === 6
        ? filtered.filter(
            s =>
              s !== 'screen_movie_top3_intro' &&
              s !== 'screen_series_top3_intro'
          )
        : filtered;

    const bilanUserIndex = filteredForPagination.indexOf('screen_bilan_user');
    if (bilanUserIndex !== -1) {
      return filteredForPagination.slice(0, bilanUserIndex + 1);
    }
    return filteredForPagination;
  }, [screenList, hiddenScreens, flashbackType]);

  const isLastStep = useMemo(() => {
    if (screenName === 'rewind_captif') {
      return true;
    }
    if (screenName === 'screen_bilan_user') {
      return true;
    }
    if (screenName === 'screen_conclusion_global') {
      return false;
    }
    if (
      flashbackType === 6 &&
      (screenName === 'screen_movie_top3' ||
        screenName === 'screen_series_top3')
    ) {
      return false;
    }
    const currentIndex = paginatedScreens.indexOf(screenName);
    return currentIndex === paginatedScreens.length - 1;
  }, [screenName, paginatedScreens, flashbackType]);

  const pagination = useMemo(() => {
    if (!isNavigationVisible) return null;
    const totalDots = paginatedScreens.length;
    const visualStep = paginatedScreens.indexOf(screenName);

    if (visualStep === -1) return null;

    const dots = [];
    for (let i = 0; i < totalDots; i++) {
      dots.push(
        <div
          key={i}
          className={classNames(styles.paging, {
            [styles.active]: i === visualStep
          })}
          onClick={() => handleStep(screenList.indexOf(paginatedScreens[i]))}
        ></div>
      );
    }
    return <div className={styles.paginationContainer}>{dots}</div>;
  }, [
    screenName,
    screenList,
    isNavigationVisible,
    handleStep,
    paginatedScreens
  ]);

  const topContentType = useMemo(() => {
    const topScreen = apiScreens.find(s => s?.name?.startsWith('screen_top_'));
    return topScreen?.name?.includes('series') ? 'series' : 'movie';
  }, [apiScreens]);

  const dynamicSlideClass = useMemo(() => {
    if (!screenName) return '';
    if (screenName.startsWith('top_content_')) {
      const slideIdentifier = screenName
        .replace('top_content_', '')
        .replace('reveal_', '');
      return `slide-top-${topContentType}-${slideIdentifier}`;
    }
    return `slide-${screenName.replaceAll('_', '-')}`;
  }, [screenName, topContentType]);

  const trackingScreenName = useMemo(() => {
    if (!screenName) return undefined;

    const trackingMap: Record<string, string> = {
      screen_upload_data: 'screen_upload_data',
      flashback_type: 'screen_intro_rewind',
      top_content_intro:
        topContentType === 'series'
          ? 'screen_top_series_2025'
          : 'screen_top_movies_2025',
      top_content_reveal_1:
        topContentType === 'series'
          ? 'screen_top_series_2025_5'
          : 'screen_top_movies_2025_5',
      top_content_reveal_2:
        topContentType === 'series'
          ? 'screen_top_series_2025_3'
          : 'screen_top_movies_2025_3',
      top_content_reveal_3:
        topContentType === 'series'
          ? 'screen_top_series_2025_1'
          : 'screen_top_movies_2025_1',
      screen_average_rating: 'screen_average_rating',
      screen_viewing_time: 'screen_viewing_time',
      screen_repartition: 'screen_repartition',
      screen_user_ranking: 'screen_user_ranking',
      screen_bilan_user: 'screen_bilan_user',
      rewind_captif: 'screen_end',
      screen_series_comparison: 'screen_series_comparison',
      screen_movie_comparison: 'screen_movie_comparison',
      favorite_celebrity_intro: 'screen_intro_star',
      favorite_celebrity_actor:
        topContentType === 'series'
          ? 'screen_favorite_series_actor'
          : topContentType === 'movie'
            ? 'screen_favorite_movie_actor'
            : 'screen_top_actor_2025',
      favorite_celebrity_director:
        topContentType === 'series'
          ? 'screen_favorite_series_director'
          : topContentType === 'movie'
            ? 'screen_favorite_movie_director'
            : 'screen_top_director_2025',
      screen_top_theater_movies_2025: 'screen_top_theater_movies_2025',
      screen_top_series_online_2025: 'screen_top_series_online_2025',
      top10_series_intro: 'screen_top10_series_intro',
      top10_series_reveal: 'screen_top10_series',
      top10_movies_intro: 'screen_top10_movie_intro',
      top10_movies_reveal: 'screen_top10_movies',
      screen_conclusion: 'screen_intro_bilan',
      screen_conclusion_global: 'screen_intro_bilan_global',
      screen_series_top3: 'screen_series_top3',
      screen_movie_top3: 'screen_movie_top3',
      screen_movie_top3_intro: 'screen_movie_top3_intro',
      screen_series_top3_intro: 'screen_series_top3_intro'
    };

    return trackingMap[screenName] || screenName.replace('screen_', '');
  }, [screenName, topContentType]);

  useEffect(() => {
    onCloseParams<RewindModalParams>({
      screenName,
      flashbackType,
      trackingScreenName
    });
  }, [onCloseParams, screenName, flashbackType, trackingScreenName]);

  return (
    <ReactModal
      {...headerLogo}
      className={classNames(
        styles.rewindModal,
        styles[camelCase(screenName)],
        dynamicSlideClass
      )}
      pagination={pagination}
      style={backgroundImage}
    >
      {loaded && screenName && (
        <RewindModalComponent
          closeModal={closeModal}
          dataLoadingStatus={dataLoadingStatus}
          onNext={handleNext}
          onStep={handleStep}
          screenName={screenName}
          isLastStep={isLastStep}
          allScreens={apiScreens}
          userNickname={userData?.me?.user?.nickname ?? undefined}
          step={step}
          steps={steps}
          ope={ope}
          images={images}
          headerLogo={headerLogo}
          flashbackType={flashbackType}
          trackingScreenName={trackingScreenName}
        />
      )}
    </ReactModal>
  );
};

export default buildReactDomModalModule(
  ModalNames.REWIND_MODAL,
  (_, closeModal, _askBeforeClosing, onCloseParams) => (
    <RewindModal closeModal={closeModal} onCloseParams={onCloseParams} />
  )
);
